import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import useDebounce from "../../../../hooks/useDebounce";
import type { SelectFieldProps } from "../SelectField";

export function useSelectField(props: SelectFieldProps) {
  const {
    options,
    searchable,
    value,
    onChange,
    multiple,
    maxSelected = Infinity,
    disabled,
    readOnly,
  } = props;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 200);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [showSuccess, setShowSuccess] = useState(false);
  const successHideTimeout = useRef<NodeJS.Timeout | null>(null);

  // Filtragem de opções
  const filteredOptions = useMemo(() => {
    if (!searchable) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
    );
  }, [options, debouncedSearchQuery, searchable]);

  // Seleção de opção
  const handleOptionSelect = useCallback(
    (optionValue: string) => {
      if (disabled || readOnly) return;
      const option = options.find(opt => opt.value === optionValue);
      if (option?.disabled) return;

      if (multiple) {
        let newValue: string[] = Array.isArray(value) ? [...value] : [];
        if (newValue.includes(optionValue)) {
          newValue = newValue.filter(v => v !== optionValue);
        } else {
          if (newValue.length >= maxSelected) return;
          newValue.push(optionValue);
        }
        onChange(newValue);
        setShowSuccess(true);
        if (successHideTimeout.current) clearTimeout(successHideTimeout.current);
        successHideTimeout.current = setTimeout(() => setShowSuccess(false), 800);
      } else {
        if (optionValue !== value) {
          onChange(optionValue);
          setShowSuccess(true);
          if (successHideTimeout.current) clearTimeout(successHideTimeout.current);
          successHideTimeout.current = setTimeout(() => setShowSuccess(false), 800);
        }
        setIsDropdownOpen(false);
      }
    },
    [onChange, value, disabled, readOnly, multiple, options, maxSelected]
  );
  useEffect(() => {
    return () => {
      if (successHideTimeout.current) clearTimeout(successHideTimeout.current);
    };
  }, []);

  return {
    isDropdownOpen,
    setIsDropdownOpen,
    searchQuery,
    setSearchQuery,
    debouncedSearchQuery,
    highlightedIndex,
    setHighlightedIndex,
    showSuccess,
    setShowSuccess,
    filteredOptions,
    handleOptionSelect,
  };
}